import React, { forwardRef, useRef, useEffect, useState } from 'react';
import { FiClipboard, FiPlus } from 'react-icons/fi';
import styles from './DataGrid.module.css';
import { DataGridColumn, DataGridProps, SortState } from './types';
import { SortIcon } from './SortIcon';
import { SkeletonRow } from './SkeletonRow';
import { Button } from '../button';
import { Modal } from '../modal';

const ClipboardIcon = FiClipboard as React.FC;
const PlusIcon = FiPlus as React.FC;

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className={styles.checkbox}
      checked={checked}
      onChange={onChange}
    />
  );
}

function DataGridInner<T extends object>(
  {
    columns,
    data,
    rowKey,
    loading = false,
    loadingRows = 5,
    striped = false,
    emptyMessage = 'No data',
    onRowClick,
    defaultSort,
    onSortChange,
    showIndex = false,
    title,
    toolbarActions,
    onAddRow,
    addRowLabel = 'Add row',
    onPasteFromClipboard,
    editableRows = false,
    editRowKey,
    editOnRowClick = true,
    commitOnBlur = true,
    commitOnEnter = true,
    commitEditSignal = 0,
    onEditChange,
    onRowSave,
    onFill,
    selectable = false,
    isRowSelectable,
    selectedKeys: controlledSelectedKeys,
    onSelectionChange,
    className,
    maxHeight,
    minWidth,
    ...rest
  }: DataGridProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [internalSort, setInternalSort] = useState<SortState | null>(defaultSort ?? null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [initialEditValues, setInitialEditValues] = useState<Record<string, string>>({});
  const [internalSelectedKeys, setInternalSelectedKeys] = useState<Set<string | number>>(new Set());
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const pendingEditRef = useRef(false);

  type FillDrag = {
    columnKey: string;
    sourceIndex: number;
    currentIndex: number;
    direction: 'up' | 'down';
  };
  const [fillDrag, setFillDrag] = useState<FillDrag | null>(null);
  const fillDragRef = useRef<FillDrag | null>(null);
  const setFill = (next: FillDrag | null) => {
    fillDragRef.current = next;
    setFillDrag(next);
  };

  const startFill = (columnKey: string, sourceIndex: number, direction: 'up' | 'down') =>
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setFill({ columnKey, sourceIndex, currentIndex: sourceIndex, direction });
    };

  const isFillTarget = (columnKey: string, rowIndex: number): boolean => {
    if (!fillDrag || fillDrag.columnKey !== columnKey) return false;
    const { sourceIndex, currentIndex, direction } = fillDrag;
    return direction === 'down'
      ? rowIndex > sourceIndex && rowIndex <= currentIndex
      : rowIndex >= currentIndex && rowIndex < sourceIndex;
  };

  const dragging = fillDrag !== null;
  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e: PointerEvent) => {
      const target = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const rowEl = target?.closest('[data-row-index]');
      const cur = fillDragRef.current;
      if (!rowEl || !cur) return;
      const idx = Number(rowEl.getAttribute('data-row-index'));
      if (Number.isNaN(idx)) return;
      const clamped =
        cur.direction === 'down' ? Math.max(cur.sourceIndex, idx) : Math.min(cur.sourceIndex, idx);
      if (clamped !== cur.currentIndex) setFill({ ...cur, currentIndex: clamped });
    };

    const handleUp = () => {
      const cur = fillDragRef.current;
      setFill(null);
      if (!cur || !onFill) return;

      const targets: number[] = [];
      if (cur.direction === 'down') {
        for (let i = cur.sourceIndex + 1; i <= cur.currentIndex; i += 1) targets.push(i);
      } else {
        for (let i = cur.currentIndex; i <= cur.sourceIndex - 1; i += 1) targets.push(i);
      }
      if (targets.length === 0) return;

      const sourceValue = (data[cur.sourceIndex] as Record<string, unknown>)[cur.columnKey];
      onFill(
        targets.map(index => ({
          index,
          row: { ...data[index], [cur.columnKey]: sourceValue } as T,
        })),
      );
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging, data, onFill]);

  useEffect(() => {
    if (!editableRows || !pendingEditRef.current || data.length === 0) return;
    pendingEditRef.current = false;
    const lastIndex = data.length - 1;
    const vals: Record<string, string> = {};
    columns.forEach(col => {
      vals[col.key] = String((data[lastIndex] as Record<string, unknown>)[col.key] ?? '');
    });
    setEditingIndex(lastIndex);
    setEditValues(vals);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isControlled = controlledSelectedKeys !== undefined;
  const selectedKeys = isControlled ? controlledSelectedKeys : internalSelectedKeys;

  const setSelectedKeys = (next: Set<string | number>) => {
    if (!isControlled) setInternalSelectedKeys(next);
    onSelectionChange?.(next);
  };

  const sort = internalSort;

  const handleSort = (key: string) => {
    let next: SortState | null;

    if (sort?.key !== key) {
      next = { key, direction: 'asc' };
    } else if (sort.direction === 'asc') {
      next = { key, direction: 'desc' };
    } else {
      next = null;
    }

    setInternalSort(next);
    onSortChange?.(next);
  };

  const getRowKey = (row: T, index: number): string | number =>
    typeof rowKey === 'function' ? rowKey(row, index) : (row[rowKey] as string | number);

  const getCellValue = (col: (typeof columns)[number], row: T, index: number): React.ReactNode => {
    if (col.getValue) return col.getValue(row, index);
    return (row as Record<string, unknown>)[col.key] as React.ReactNode;
  };

  const startEdit = (row: T, index: number) => {
    const vals: Record<string, string> = {};
    columns.forEach(col => {
      vals[col.key] = String((row as Record<string, unknown>)[col.key] ?? '');
    });
    setEditingIndex(index);
    setEditValues(vals);
    setInitialEditValues(vals);
    onEditChange?.(row, index, false);
  };

  useEffect(() => {
    if (!editableRows || editRowKey == null) return;
    const index = data.findIndex((row, rowIndex) => getRowKey(row, rowIndex) === editRowKey);
    if (index >= 0 && editingIndex !== index) startEdit(data[index], index);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRowKey, editableRows, data]);

  const commitEdit = (row: T, index: number) => {
    setEditingIndex(null);
    const updatedRow = { ...row, ...editValues } as T;
    onRowSave?.(updatedRow, index);
  };

  useEffect(() => {
    if (commitEditSignal === 0 || editingIndex == null) return;
    commitEdit(data[editingIndex], editingIndex);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitEditSignal]);

  const handleRowBlur = (e: React.FocusEvent<HTMLTableRowElement>, row: T, index: number) => {
    const related = e.relatedTarget as HTMLElement | null;
    if (!related?.closest(`[data-row-index="${index}"]`)) {
      commitEdit(row, index);
    }
  };

  const handleRowClick = (row: T, index: number) => {
    if (editableRows && editOnRowClick) {
      if (editingIndex !== index) startEdit(row, index);
      return;
    }
    onRowClick?.(row, index);
  };

  const handleToggleRow = (key: string | number, row: T, index: number) => {
    if (isRowSelectable && !isRowSelectable(row, index)) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedKeys(next);
  };

  const allKeys = data
    .map((row, index) => ({ row, index, key: getRowKey(row, index) }))
    .filter(({ row, index }) => !isRowSelectable || isRowSelectable(row, index))
    .map(({ key }) => key);
  const allSelected = allKeys.length > 0 && allKeys.every(k => selectedKeys.has(k));
  const someSelected = allKeys.some(k => selectedKeys.has(k)) && !allSelected;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(allKeys));
    }
  };

  const isEmpty = !loading && data.length === 0;
  const totalCols = columns.length + (showIndex ? 1 : 0) + (selectable ? 1 : 0);

  return (
    <div ref={ref} className={cx(styles.wrapper, striped && styles.striped, className)} {...rest}>
      {(title || toolbarActions || onAddRow || onPasteFromClipboard) && (
        <div className={styles.toolbar}>
          <span className={styles.toolbarTitle}>{title}</span>
          <div className={styles.toolbarActions}>
            {onPasteFromClipboard && (
              <Button
                variant="secondary"
                startIcon={<ClipboardIcon />}
                onClick={async () => {
                  setEditingIndex(null);
                  setEditValues({});
                  try {
                    const text = await navigator.clipboard.readText();
                    onPasteFromClipboard(text);
                  } catch {
                    setPasteText('');
                    setPasteModalOpen(true);
                  }
                }}
              >
                Paste from Excel
              </Button>
            )}
            {onAddRow && (
              <Button variant="secondary" startIcon={<PlusIcon />} onClick={() => { pendingEditRef.current = true; onAddRow(); }}>
                {addRowLabel}
              </Button>
            )}
            {toolbarActions}
          </div>
        </div>
      )}
      <div
        className={cx(styles.scrollArea, maxHeight != null && styles.scrollAreaBounded)}
        style={maxHeight != null ? { maxHeight } : undefined}
      >
        <table
          className={styles.table}
          style={minWidth != null ? { minWidth } : undefined}
        >
          <colgroup>
            {selectable && <col className={styles.checkboxCol} />}
            {showIndex && <col className={styles.indexCol} />}
            {columns.map(col => (
              <col key={col.key} style={{ width: col.width, minWidth: col.minWidth }} />
            ))}
          </colgroup>
          <thead className={cx(styles.thead, maxHeight != null && styles.theadSticky)}>
            <tr>
              {selectable && (
                <th
                  className={cx(styles.th, styles.checkboxTh, styles['align-center'])}
                  onClick={handleToggleAll}
                >
                  <IndeterminateCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={() => {}}
                  />
                </th>
              )}
              {showIndex && (
                <th className={cx(styles.th, styles.indexTh, styles['align-center'])}>
                  <span className={styles.thInner}>#</span>
                </th>
              )}
              {columns.map(col => {
                const isSortable = col.sortable ?? false;
                const isActive = sort?.key === col.key;

                return (
                  <th
                    key={col.key}
                    className={cx(
                      styles.th,
                      styles[`align-${col.align ?? 'left'}`],
                      isSortable && styles.thSortable,
                    )}
                    style={{ width: col.width, minWidth: col.minWidth }}
                    aria-sort={
                      isActive
                        ? sort?.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : isSortable
                        ? 'none'
                        : undefined
                    }
                    onClick={isSortable ? () => handleSort(col.key) : undefined}
                  >
                    <span className={styles.thInner}>
                      {col.header}
                      {isSortable && (
                        <SortIcon
                          direction={isActive ? sort?.direction : undefined}
                          active={isActive}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className={styles.tbody}>
            {loading
              ? Array.from({ length: loadingRows }, (_, i) => (
                  <SkeletonRow key={i} columns={columns as DataGridColumn<object>[]} showIndex={showIndex} selectable={selectable} />
                ))
              : isEmpty
              ? (
                <tr>
                  <td colSpan={totalCols} className={styles.emptyCell}>
                    <span className={styles.emptyMessage}>{emptyMessage}</span>
                  </td>
                </tr>
              )
              : data.map((row, index) => {
                  const key = getRowKey(row, index);
                  const isEditing = editableRows && editingIndex === index;
                  const isClickable = (editableRows && editOnRowClick) || !!onRowClick;
                  const rowSelectable = !isRowSelectable || isRowSelectable(row, index);
                  const isSelected = selectable && selectedKeys.has(key);

                  return (
                    <tr
                      key={key}
                      data-row-index={editableRows ? index : undefined}
                      className={cx(
                        styles.row,
                        isClickable && styles.rowClickable,
                        isEditing && styles.rowEditing,
                        isSelected && styles.rowSelected,
                      )}
                      onClick={isClickable ? () => handleRowClick(row, index) : undefined}
                      onBlur={
                        isEditing && commitOnBlur
                          ? e => handleRowBlur(e, row, index)
                          : undefined
                      }
                      tabIndex={isClickable ? 0 : undefined}
                      onKeyDown={
                        isEditing
                          ? e => {
                              if (e.key === 'Escape') {
                                setEditingIndex(null);
                                setEditValues({});
                              } else if (e.key === 'Enter' && commitOnEnter) {
                                commitEdit(row, index);
                              }
                            }
                          : !editableRows && onRowClick
                          ? e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick(row, index);
                              }
                            }
                          : undefined
                      }
                    >
                      {selectable && (
                        <td
                          className={cx(styles.cell, styles.checkboxCell, styles['align-center'])}
                          onClick={e => {
                            e.stopPropagation();
                            handleToggleRow(key, row, index);
                          }}
                        >
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={isSelected}
                            disabled={!rowSelectable}
                            onChange={() => {}}
                          />
                        </td>
                      )}
                      {showIndex && (
                        <td className={cx(styles.cell, styles.indexCell, styles['align-center'])}>
                          {index + 1}
                        </td>
                      )}
                      {columns.map(col => {
                        const isColEditable = isEditing && col.editable !== false;
                        const showFillHandle =
                          editableRows && !!onFill && col.editable !== false;

                        return (
                          <td
                            key={col.key}
                            className={cx(
                              styles.cell,
                              styles[`align-${col.align ?? 'left'}`],
                              isColEditable && styles.cellEditing,
                              isFillTarget(col.key, index) && styles.fillTarget,
                            )}
                            style={{ width: col.width, minWidth: col.minWidth }}
                          >
                            {isColEditable ? (
                              <input
                                className={styles.cellInput}
                                aria-label={
                                  typeof col.header === 'string' ? col.header : col.key
                                }
                                value={editValues[col.key] ?? ''}
                                onChange={e => {
                                  const raw = e.target.value;
                                  const value = col.sanitizeInput ? col.sanitizeInput(raw) : raw;
                                  const next = { ...editValues, [col.key]: value };
                                  const updatedRow = { ...row, ...next } as T;
                                  const dirty = Object.keys(next).some(
                                    key => next[key] !== initialEditValues[key],
                                  );
                                  setEditValues(next);
                                  onEditChange?.(updatedRow, index, dirty);
                                }}
                                onPaste={
                                  col.onCellPaste
                                    ? e => {
                                        setEditingIndex(null);
                                        setEditValues({});
                                        col.onCellPaste!(e, index);
                                      }
                                    : undefined
                                }
                                onClick={e => e.stopPropagation()}
                                autoFocus={columns.indexOf(col) === 0}
                              />
                            ) : (
                              getCellValue(col, row, index)
                            )}
                            {showFillHandle && (
                              <>
                                <span
                                  className={cx(styles.fillHandle, styles.fillHandleTop)}
                                  onPointerDown={startFill(col.key, index, 'up')}
                                  onClick={e => e.stopPropagation()}
                                  aria-hidden="true"
                                />
                                <span
                                  className={cx(styles.fillHandle, styles.fillHandleBottom)}
                                  onPointerDown={startFill(col.key, index, 'down')}
                                  onClick={e => e.stopPropagation()}
                                  aria-hidden="true"
                                />
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
      {onAddRow && (
        <div className={styles.addRowFooter}>
          <Button variant="secondary" fullWidth startIcon={<PlusIcon />} onClick={() => { pendingEditRef.current = true; onAddRow(); }}>
            {addRowLabel}
          </Button>
        </div>
      )}
      {onPasteFromClipboard && (
        <Modal
          open={pasteModalOpen}
          onClose={() => setPasteModalOpen(false)}
          title="Paste from Excel"
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={() => setPasteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (pasteText.trim()) onPasteFromClipboard(pasteText);
                  setPasteModalOpen(false);
                }}
              >
                Paste
              </Button>
            </>
          }
        >
          <p className={styles.pasteModalHint}>
            Copy your data from Excel and paste it here (Ctrl+V / Cmd+V).
          </p>
          <textarea
            className={styles.pasteTextarea}
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
            onPaste={e => {
              e.stopPropagation();
              const text = e.clipboardData.getData('text/plain');
              setPasteText(text);
            }}
            rows={10}
            placeholder="Paste Excel data here..."
            autoFocus
          />
        </Modal>
      )}
    </div>
  );
}

export const DataGrid = forwardRef(DataGridInner) as <T extends object>(
  props: DataGridProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;

(DataGrid as unknown as { displayName: string }).displayName = 'DataGrid';
